import { ReactNode, createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PlaylistItem {
    type: 'summary' | 'email';
    id: string;
    url: string;
    title: string;
}

interface DigestPlayerState {
    activeDigestId: string | null;
    activeDigestWorkflowUuid: string | null;
    activeDigestWorkflowName: string | null;
    activeDigestWorkflowColor: Modules.Workflows.Enums.WorkflowColorEnum | null;
    playlist: PlaylistItem[];
    currentItemIndex: number | null;
    isPlaying: boolean;
    isLoading: boolean;
    currentTime: number;
    duration: number;
    playbackSpeed: number;
    volume: number;
    error: string | null;
}

interface DigestPlayerContextType extends DigestPlayerState {
    play: (digestData: Modules.Digests.Data.DigestData, emailsData: Modules.Digests.Data.DigestEmailData[]) => void;
    playDigestSummary: (digestData: Modules.Digests.Data.DigestData) => void;
    pause: () => void;
    togglePlayPause: () => void;
    next: () => void;
    previous: () => void;
    seek: (time: number) => void;
    setVolume: (volume: number) => void;
    setPlaybackSpeed: (speed: number) => void;
    setState: React.Dispatch<React.SetStateAction<DigestPlayerState>>;
}

const getErrorName = (error: unknown): string | undefined =>
    typeof error === 'object' && error !== null && 'name' in error && typeof error.name === 'string' ? error.name : undefined;

const initialState: DigestPlayerState = {
    activeDigestId: null,
    activeDigestWorkflowUuid: null,
    activeDigestWorkflowName: null,
    activeDigestWorkflowColor: null,
    playlist: [],
    currentItemIndex: null,
    isPlaying: false,
    isLoading: false,
    currentTime: 0,
    duration: 0,
    playbackSpeed: 1,
    volume: 1,
    error: null,
};

const ssrSafeInitialState: DigestPlayerContextType = {
    ...initialState,
    play: () => {
        console.warn('[SSR] play called on DigestPlayer');
    },
    playDigestSummary: () => {
        console.warn('[SSR] playDigestSummary called on DigestPlayer');
    },
    pause: () => {
        console.warn('[SSR] pause called on DigestPlayer');
    },
    togglePlayPause: () => {
        console.warn('[SSR] togglePlayPause called on DigestPlayer');
    },
    next: () => {
        console.warn('[SSR] next called on DigestPlayer');
    },
    previous: () => {
        console.warn('[SSR] previous called on DigestPlayer');
    },
    seek: (_time: number) => {
        void _time;
        console.warn('[SSR] seek called on DigestPlayer');
    },
    setVolume: (_volume: number) => {
        void _volume;
        console.warn('[SSR] setVolume called on DigestPlayer');
    },
    setPlaybackSpeed: (_speed: number) => {
        void _speed;
        console.warn('[SSR] setPlaybackSpeed called on DigestPlayer');
    },
    setState: (_action: React.SetStateAction<DigestPlayerState>) => {
        void _action;
        console.warn('[SSR] setState called on DigestPlayer');
    },
};

const DigestPlayerContext = createContext<DigestPlayerContextType | undefined>(undefined);

export const useDigestPlayer = () => {
    const context = useContext(DigestPlayerContext);
    if (context === undefined) {
        if (typeof window === 'undefined') {
            return ssrSafeInitialState;
        }
        throw new Error('useDigestPlayer must be used within a DigestPlayerProvider on the client-side.');
    }
    return context;
};

export const DigestPlayerProvider = ({ children }: { children: ReactNode }) => {
    const { t } = useTranslation('Common');
    const [state, setState] = useState<DigestPlayerState>(() => {
        const savedState = localStorage.getItem('digestPlayerState');
        if (savedState) {
            try {
                const parsed = JSON.parse(savedState);
                return {
                    ...initialState,
                    activeDigestId: parsed.activeDigestId || null,
                    currentTime: parsed.currentTime || 0,
                    playbackSpeed: parsed.playbackSpeed || 1,
                    volume: parsed.volume || 1,
                    isPlaying: false,
                    isLoading: false,
                    playlist: [],
                    currentItemIndex: null,
                    activeDigestWorkflowUuid: null,
                    activeDigestWorkflowName: null,
                    activeDigestWorkflowColor: null,
                    error: null,
                };
            } catch (e) {
                console.error('Failed to parse saved digest player state', e);
            }
        }
        return initialState;
    });

    const audioRef = useRef<HTMLAudioElement | null>(null);
    const playPromiseRef = useRef<Promise<void> | null>(null);
    const navigationTimeoutRef = useRef<number | null>(null);
    const { activeDigestId, playlist, currentItemIndex, isPlaying, currentTime, duration, playbackSpeed, volume } = state;

    useEffect(() => {
        if (!audioRef.current) {
            audioRef.current = new Audio();
        }
        const audioElement = audioRef.current;

        const handleTimeUpdate = () => {
            if (audioElement) {
                const newTime = audioElement.currentTime;
                if (typeof newTime === 'number' && isFinite(newTime)) {
                    setState((prev) => ({ ...prev, currentTime: newTime }));
                }
            }
        };

        const handleDurationChange = () => {
            if (audioElement) {
                const newDuration = audioElement.duration;
                if (typeof newDuration === 'number' && isFinite(newDuration)) {
                    setState((prev) => ({ ...prev, duration: newDuration }));
                }
            }
        };

        const handleLoadStart = () => {
            setState((prev) => ({ ...prev, isLoading: true }));
        };

        const handleCanPlay = () => {
            setState((prev) => ({ ...prev, isLoading: false }));
        };

        const handleWaiting = () => {
            setState((prev) => ({ ...prev, isLoading: true }));
        };

        const handlePlaying = () => {
            setState((prev) => ({ ...prev, isLoading: false }));
        };

        const handleError = (event: Event) => {
            console.error('Audio playback error:', event);
            const mediaError = (event.target as HTMLAudioElement)?.error;
            const errorMessage = mediaError
                ? t('digest_player.media_error', { code: mediaError.code, message: mediaError.message })
                : t('digest_player.playback_error');
            setState((prev) => ({ ...prev, isPlaying: false, isLoading: false, error: errorMessage }));
        };

        const handleEnded = () => {
            setState((prev) => {
                if (prev.currentItemIndex !== null && prev.currentItemIndex < prev.playlist.length - 1) {
                    return { ...prev, currentItemIndex: prev.currentItemIndex + 1, currentTime: 0, isPlaying: true, isLoading: false };
                } else {
                    return { ...prev, isPlaying: false, isLoading: false, currentItemIndex: null, activeDigestId: null, currentTime: 0 };
                }
            });
        };

        audioElement.addEventListener('timeupdate', handleTimeUpdate);
        audioElement.addEventListener('durationchange', handleDurationChange);
        audioElement.addEventListener('loadstart', handleLoadStart);
        audioElement.addEventListener('canplay', handleCanPlay);
        audioElement.addEventListener('waiting', handleWaiting);
        audioElement.addEventListener('playing', handlePlaying);
        audioElement.addEventListener('ended', handleEnded);
        audioElement.addEventListener('error', handleError);

        return () => {
            audioElement.removeEventListener('timeupdate', handleTimeUpdate);
            audioElement.removeEventListener('durationchange', handleDurationChange);
            audioElement.removeEventListener('loadstart', handleLoadStart);
            audioElement.removeEventListener('canplay', handleCanPlay);
            audioElement.removeEventListener('waiting', handleWaiting);
            audioElement.removeEventListener('playing', handlePlaying);
            audioElement.removeEventListener('ended', handleEnded);
            audioElement.removeEventListener('error', handleError);
        };
    }, []);

    useEffect(() => {
        const stateToSave = {
            activeDigestId,
            currentTime,
            playbackSpeed,
            volume,
        };
        localStorage.setItem('digestPlayerState', JSON.stringify(stateToSave));
    }, [activeDigestId, currentTime, playbackSpeed, volume]);

    useEffect(() => {
        return () => {
            if (navigationTimeoutRef.current) {
                clearTimeout(navigationTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!audioRef.current) {
            return;
        }

        let currentUrl = '';
        if (currentItemIndex !== null && playlist.length > currentItemIndex) {
            currentUrl = playlist[currentItemIndex].url;
        }

        const audioElement = audioRef.current;

        const handleAudioPlayback = async () => {
            if (currentUrl && isPlaying) {
                setState((prev) => ({ ...prev, error: null, isLoading: true }));

                if (audioElement.src !== currentUrl) {
                    setState((prev) => ({ ...prev, currentTime: 0, duration: 0 }));

                    if (playPromiseRef.current) {
                        try {
                            await playPromiseRef.current;
                        } catch (error) {
                            if (getErrorName(error) !== 'AbortError') {
                                console.warn('[DigestPlayerContext] Previous play promise rejected:', error);
                            }
                        }
                    }

                    audioElement.src = currentUrl;
                    audioElement.load();
                    audioElement.playbackRate = playbackSpeed;
                    audioElement.volume = volume;

                    try {
                        playPromiseRef.current = audioElement.play();
                        await playPromiseRef.current;
                        playPromiseRef.current = null;
                    } catch (error) {
                        playPromiseRef.current = null;
                        if (getErrorName(error) !== 'AbortError') {
                            console.error('[DigestPlayerContext] Error playing new audio:', { url: currentUrl, error });
                            setState((prev) => ({ ...prev, isPlaying: false, error: `Failed to play audio. (${getErrorName(error)})` }));
                        }
                    }
                } else if (audioElement.paused) {
                    try {
                        playPromiseRef.current = audioElement.play();
                        await playPromiseRef.current;
                        playPromiseRef.current = null;
                    } catch (error) {
                        playPromiseRef.current = null;
                        if (getErrorName(error) !== 'AbortError') {
                            console.error('[DigestPlayerContext] Error resuming audio:', { url: currentUrl, error });
                            setState((prev) => ({ ...prev, isPlaying: false, error: `Failed to resume audio. (${getErrorName(error)})` }));
                        }
                    }
                }
            } else {
                if (!audioElement.paused) {
                    audioElement.pause();
                }
                playPromiseRef.current = null;
            }
        };

        handleAudioPlayback();
    }, [currentItemIndex, playlist, isPlaying, playbackSpeed, volume]);

    const play = useCallback(
        (digestData: Modules.Digests.Data.DigestData, emailsData: Modules.Digests.Data.DigestEmailData[]) => {
            try {
                const summaryUrl = route('digest.audio', { digest: digestData.uuid });
                const newPlaylist: PlaylistItem[] = [
                    {
                        type: 'summary',
                        id: digestData.uuid,
                        url: summaryUrl,
                        title: t('digest_player.summary_title'),
                    },
                ];

                if (Array.isArray(emailsData)) {
                    emailsData.forEach((email: Modules.Digests.Data.DigestEmailData) => {
                        if (email.uuid) {
                            const emailUrl = route('digest.email.audio', {
                                digest: digestData.uuid,
                                digest_email: email.uuid,
                            });
                            newPlaylist.push({
                                type: 'email',
                                id: email.uuid,
                                url: emailUrl,
                                title: email.subject || t('digest_player.untitled_email', { uuid: email.uuid }),
                            });
                        }
                    });
                }

                setState((prev) => ({
                    ...prev,
                    activeDigestId: digestData.uuid,
                    activeDigestWorkflowUuid: digestData.workflow_uuid,
                    activeDigestWorkflowName: digestData.workflow_name,
                    activeDigestWorkflowColor: digestData.workflow_color,
                    playlist: newPlaylist,
                    currentItemIndex: 0,
                    isPlaying: true,
                    isLoading: true,
                    currentTime: 0,
                    duration: 0,
                    error: null,
                }));
            } catch (error) {
                console.error('Error building full playlist or starting playback:', error);
                setState((prev) => ({ ...prev, error: t('digest_player.full_load_error'), isPlaying: false }));
            }
        },
        [setState, t],
    );

    const playDigestSummary = useCallback(
        (digestData: Modules.Digests.Data.DigestData) => {
            try {
                const summaryUrl = route('digest.audio', { digest: digestData.uuid });
                const summaryPlaylistItem: PlaylistItem = {
                    type: 'summary',
                    id: digestData.uuid,
                    url: summaryUrl,
                    title: t('digest_player.summary_title'),
                };

                setState((prev) => ({
                    ...prev,
                    activeDigestId: digestData.uuid,
                    activeDigestWorkflowUuid: digestData.workflow_uuid,
                    activeDigestWorkflowName: digestData.workflow_name,
                    activeDigestWorkflowColor: digestData.workflow_color,
                    playlist: [summaryPlaylistItem],
                    currentItemIndex: 0,
                    isPlaying: true,
                    isLoading: true,
                    currentTime: 0,
                    duration: 0,
                    error: null,
                }));
            } catch (error) {
                console.error('Error starting summary playback:', error);
                setState((prev) => ({ ...prev, error: t('digest_player.load_error'), isPlaying: false }));
            }
        },
        [setState, t],
    );

    const pause = useCallback(() => {
        setState((prev) => ({ ...prev, isPlaying: false, isLoading: false }));
    }, [setState]);

    const togglePlayPause = useCallback(() => {
        if (!isPlaying && currentItemIndex === null && playlist.length > 0) {
            setState((prev) => ({
                ...prev,
                currentItemIndex: 0,
                isPlaying: true,
                isLoading: true,
                currentTime: 0,
                error: null,
            }));
        } else {
            setState((prev) => ({ ...prev, isPlaying: !prev.isPlaying, isLoading: !prev.isPlaying }));
        }
    }, [isPlaying, currentItemIndex, playlist, setState]);

    const next = useCallback(() => {
        if (navigationTimeoutRef.current) {
            clearTimeout(navigationTimeoutRef.current);
        }

        navigationTimeoutRef.current = window.setTimeout(() => {
            setState((prev) => {
                if (prev.currentItemIndex !== null && prev.currentItemIndex < prev.playlist.length - 1) {
                    return {
                        ...prev,
                        currentItemIndex: prev.currentItemIndex + 1,
                        currentTime: 0,
                        isPlaying: true,
                        isLoading: true,
                        error: null,
                    };
                }
                return { ...prev, isPlaying: false };
            });
        }, 100);
    }, [setState]);

    const previous = useCallback(() => {
        if (navigationTimeoutRef.current) {
            clearTimeout(navigationTimeoutRef.current);
        }

        navigationTimeoutRef.current = window.setTimeout(() => {
            setState((prev) => {
                if (prev.currentItemIndex !== null && prev.currentItemIndex > 0) {
                    return {
                        ...prev,
                        currentItemIndex: prev.currentItemIndex - 1,
                        currentTime: 0,
                        isPlaying: true,
                        isLoading: true,
                        error: null,
                    };
                }
                return { ...prev };
            });
        }, 100);
    }, [setState]);

    const seek = useCallback(
        (time: number) => {
            if (audioRef.current) {
                const newTime = Math.max(0, Math.min(time, duration || 0));
                audioRef.current.currentTime = newTime;
                setState((prev) => ({ ...prev, currentTime: newTime }));
            }
        },
        [duration, setState],
    );

    const setVolume = useCallback(
        (volumeValue: number) => {
            const newVolume = Math.max(0, Math.min(volumeValue, 1));
            setState((prev) => ({ ...prev, volume: newVolume }));
            if (audioRef.current) {
                audioRef.current.volume = newVolume;
            }
        },
        [setState],
    );

    const setPlaybackSpeed = useCallback(
        (speed: number) => {
            const newSpeed = Math.max(0.5, Math.min(speed, 4));
            setState((prev) => ({ ...prev, playbackSpeed: newSpeed }));
            if (audioRef.current) {
                audioRef.current.playbackRate = newSpeed;
            }
        },
        [setState],
    );

    const contextValue: DigestPlayerContextType = {
        ...state,
        play,
        playDigestSummary,
        pause,
        togglePlayPause,
        next,
        previous,
        seek,
        setVolume,
        setPlaybackSpeed,
        setState,
    };

    return (
        <DigestPlayerContext.Provider value={contextValue}>
            {children}
            <audio ref={audioRef} />
        </DigestPlayerContext.Provider>
    );
};

export { DigestPlayerContext };
export default DigestPlayerProvider;
